'use client';
import { Credits } from '@/components/Credits';
import { AmountSentence } from '@/components/AmountSentence';
import { useLocalTranslation } from '@/hooks/useLocalTranslation';
import { pluralize } from '@/i18n/pluralize';
import { FaGift } from '@/components/icons';
import { useOpenClaimSheet } from '@/stores/claimSheetStore';
import translations from './AchievementClaimBanner.translations';
import { useClaimableRewards, type ClaimableAchievementRewards } from '../hooks/useClaimableRewards';

interface ViewProps {
  data: ClaimableAchievementRewards;
  claiming?: boolean;
  onClaim: () => void;
  onDeposit: () => void;
}

export function AchievementClaimBannerView({ data, claiming, onClaim, onDeposit }: ViewProps) {
  const { t, lang } = useLocalTranslation(translations);
  const achWord = (n: number) =>
    pluralize(n, { one: t('achOne'), few: t('achFew'), many: t('achMany') }, lang);
  if (data.count <= 0) return null;

  const pct =
    data.requiredDeposit > 0
      ? Math.min(100, Math.round((data.totalDepositCredits / data.requiredDeposit) * 100))
      : 100;

  return (
    <div className="relative overflow-hidden rounded-2xl bg-fill-tertiary p-4">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(120%_120%_at_0%_0%,rgba(245,158,11,0.18),transparent_60%)]" />
      <div className="relative">
        <div className="flex flex-wrap items-center gap-x-1.5 text-sm text-label-secondary">
          <Credits
            amount={data.claimableCoins}
            iconSize={15}
            className="text-base font-bold text-label-primary"
          />
          {data.claimableXp > 0 && <span>· +{data.claimableXp} XP</span>}
          <span>
            · {data.count} {achWord(data.count)}
          </span>
        </div>

        {data.canClaim ? (
          <button
            type="button"
            onClick={onClaim}
            disabled={claiming}
            className="animate-shot-claim-glow mt-3 flex w-full items-center justify-center gap-1.5 rounded-xl bg-gradient-to-r from-amber-400 to-amber-500 px-4 py-2.5 text-sm font-bold text-black transition disabled:opacity-80"
          >
            {claiming ? (
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-black/30 border-t-black" />
            ) : (
              <>
                {t('claim')} <Credits amount={data.claimableCoins} iconSize={14} />
              </>
            )}
          </button>
        ) : (
          <>
            <div className="mt-3 h-2 overflow-hidden rounded-full bg-white/10">
              <div
                className="h-full rounded-full bg-gradient-to-r from-amber-400 to-amber-500 transition-[width] duration-500"
                style={{ width: `${pct}%` }}
              />
            </div>

            <AmountSentence
              template={t('needMoreToClaim')}
              amount={data.depositShortfall}
              iconSize={12}
              className="mt-2 block text-xs text-label-secondary"
            />
            <button
              type="button"
              onClick={onDeposit}
              className="mt-3 w-full rounded-xl bg-price px-4 py-2.5 text-sm font-bold text-black transition active:opacity-80"
            >
              {t('deposit')}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export function AchievementClaimBanner() {
  const { t, lang } = useLocalTranslation(translations);
  const achWord = (n: number) =>
    pluralize(n, { one: t('achOne'), few: t('achFew'), many: t('achMany') }, lang);
  const { data } = useClaimableRewards();
  const openSheet = useOpenClaimSheet();

  if (!data || data.count <= 0) return null;

  return (
    <div className="mb-4 px-4">
    <button
      type="button"
      onClick={openSheet}
      className="liquid-glass relative flex w-full items-center gap-3 overflow-hidden rounded-2xl p-3 text-start backdrop-blur-[15px] backdrop-saturate-[180%] backdrop-brightness-125 transition active:opacity-80"
    >
      <span className="pointer-events-none absolute inset-0 bg-[radial-gradient(120%_120%_at_0%_0%,rgba(245,158,11,0.16),transparent_60%)]" />
      <span className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-price/20 text-price">
        <FaGift size={16} />
      </span>
      <span className="relative min-w-0 flex-1">
        <span className="block text-sm font-semibold text-label-primary">{t('title')}</span>
        <span className="flex items-center gap-1 text-xs text-label-secondary">
          <Credits amount={data.claimableCoins} iconSize={11} />
          <span>
            · {data.count} {achWord(data.count)}
          </span>
        </span>
      </span>
      {data.canClaim && (
        <span className="relative h-2 w-2 shrink-0 rounded-full bg-price shadow-[0_0_8px_rgba(245,158,11,0.8)]" />
      )}
      <span className="relative shrink-0 text-label-tertiary">›</span>
    </button>
    </div>
  );
}
